// The part binder: moves a loaded model's joints from pose driver values.
//
// The glTF part contract (docs/contracts/gltf-parts.md § Joints and motion)
// sets each joint's local transform every frame to
//
//   joint.local(θ) = rest × D(v),   v = the joint's driver in pose_at(θ),
//
// with D(u) a turn by u radians about psim.axis (rotate), a move by u metres
// along it (translate) or a stretch by the factor 1 + u along it (scale, with
// psim.axis one of the joint's own X, Y and Z). u is the driver's value v, or
// for a joint with psim.curve the curve's value at v: linear between its
// [x, y] pairs and held at its ends (ADR 0015). GLTFLoader puts a node's
// extras in userData, so a joint is any object whose userData has
// psim.driver. Nothing else moves. bindJoints copies each curve into typed
// arrays, so apply() allocates nothing and can run every frame.

import { Object3D, Quaternion, Vector3 } from "three";

export type JointMotion = "rotate" | "translate" | "scale";

export interface BoundJoint {
  readonly node: Object3D;
  readonly driver: string;
  /** Index of the driver in the pose source's list. */
  readonly index: number;
  readonly motion: JointMotion;
  /** Unit axis in the joint's own frame. */
  readonly axis: Vector3;
  /** For a scale joint, which component of node.scale its axis is (0 x, 1 y, 2 z). */
  readonly scaleComponent: 0 | 1 | 2;
  /** psim.curve's x and y values, or null when the joint moves by the driver's value itself. */
  readonly curveX: Float64Array | null;
  readonly curveY: Float64Array | null;
  readonly restPosition: Vector3;
  readonly restQuaternion: Quaternion;
  readonly restScale: Vector3;
}

export interface BindResult {
  joints: BoundJoint[];
  /** Joints whose driver the pose source does not have: they stay at rest. */
  unbound: { node: string; driver: string }[];
}

const _q = new Quaternion();

export class PartBinder {
  readonly joints: readonly BoundJoint[];
  readonly unbound: readonly { node: string; driver: string }[];

  constructor(root: Object3D, drivers: readonly string[]) {
    const { joints, unbound } = bindJoints(root, drivers);
    this.joints = joints;
    this.unbound = unbound;
  }

  /** Sets every joint from the driver values (in the pose source's driver order). */
  apply(values: ArrayLike<number>): void {
    for (const j of this.joints) {
      const v = values[j.index]!;
      const u = j.curveX && j.curveY ? curveAt(j.curveX, j.curveY, v) : v;
      if (j.motion === "rotate") {
        j.node.position.copy(j.restPosition);
        j.node.quaternion.copy(j.restQuaternion).multiply(_q.setFromAxisAngle(j.axis, u));
      } else if (j.motion === "translate") {
        j.node.quaternion.copy(j.restQuaternion);
        j.node.position.copy(j.axis).applyQuaternion(j.restQuaternion).multiplyScalar(u).add(j.restPosition);
      } else {
        // rest × S: three.js composes position, quaternion and then scale, so the
        // stretch along the joint's own axis is a component of node.scale.
        j.node.position.copy(j.restPosition);
        j.node.quaternion.copy(j.restQuaternion);
        j.node.scale.copy(j.restScale).setComponent(j.scaleComponent, j.restScale.getComponent(j.scaleComponent) * (1 + u));
      }
    }
  }

  /** Puts every joint back at its rest transform. */
  reset(): void {
    for (const j of this.joints) {
      j.node.position.copy(j.restPosition);
      j.node.quaternion.copy(j.restQuaternion);
      j.node.scale.copy(j.restScale);
    }
  }
}

/**
 * A joint curve's value at x: linear between the pairs (xs strictly
 * increasing), held at the first or last y outside them. A binary search,
 * so a dense curve costs little; allocates nothing.
 */
export function curveAt(xs: Float64Array, ys: Float64Array, x: number): number {
  const n = xs.length;
  if (!(x > xs[0]!)) return ys[0]!;
  if (!(x < xs[n - 1]!)) return ys[n - 1]!;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xs[mid]! <= x) lo = mid;
    else hi = mid;
  }
  const x0 = xs[lo]!;
  const y0 = ys[lo]!;
  return y0 + ((ys[hi]! - y0) * (x - x0)) / (xs[hi]! - x0);
}

/** psim.curve as typed arrays, or nulls when the joint has none (gltf-check has checked its shape). */
function curveArrays(curve: unknown): { curveX: Float64Array | null; curveY: Float64Array | null } {
  if (!Array.isArray(curve) || curve.length < 2) return { curveX: null, curveY: null };
  const pairs = curve as [number, number][];
  return { curveX: Float64Array.from(pairs, (p) => p[0]), curveY: Float64Array.from(pairs, (p) => p[1]) };
}

function motionOf(value: unknown): JointMotion {
  return value === "translate" || value === "scale" ? value : "rotate";
}

/** Finds the joints under `root` and their drivers' indices. */
export function bindJoints(root: Object3D, drivers: readonly string[]): BindResult {
  const joints: BoundJoint[] = [];
  const unbound: { node: string; driver: string }[] = [];
  root.traverse((node) => {
    const driver = node.userData["psim.driver"];
    if (typeof driver !== "string") return;
    const index = drivers.indexOf(driver);
    if (index < 0) {
      unbound.push({ node: node.name, driver });
      return;
    }
    const axis = node.userData["psim.axis"] as number[];
    const unit = new Vector3(axis[0], axis[1], axis[2]).normalize();
    const along = [Math.abs(unit.x), Math.abs(unit.y), Math.abs(unit.z)];
    joints.push({
      node,
      driver,
      index,
      motion: motionOf(node.userData["psim.motion"]),
      axis: unit,
      scaleComponent: along[0]! >= along[1]! && along[0]! >= along[2]! ? 0 : along[1]! >= along[2]! ? 1 : 2,
      ...curveArrays(node.userData["psim.curve"]),
      restPosition: node.position.clone(),
      restQuaternion: node.quaternion.clone(),
      restScale: node.scale.clone(),
    });
  });
  return { joints, unbound };
}
